import { formatMoney } from "./formatMoney";
import { Calendar, DollarSign } from "lucide-react";
import { Badge } from "@mantis-sas/ui";

/**
 * Shared utility function for formatting cell values consistently across components
 * Used by both ControlPanelTable and ControlPanelDetail components
 */
export const formatCellValue = (key, value, row = null) => {
  if (value === null || value === undefined) {
    return (
      <span className="text-slate-400 dark:text-slate-500 italic">
        Sin datos
      </span>
    );
  }

  // Status formatting
  if (key === "status") {
    const statusMap = {
      complete: {
        label: "Completado",
        className: "badge-clean badge-green",
        icon: "✓",
      },
      approved: {
        label: "Aprobado",
        className: "badge-clean badge-green",
        icon: "✓",
      },
      aprobado: {
        label: "Aprobado",
        className: "badge-clean badge-green",
        icon: "✓",
      },
      pending: {
        label: "Pendiente",
        className: "badge-clean badge-amber",
        icon: "⏳",
      },
      pendiente: {
        label: "Pendiente",
        className: "badge-clean badge-amber",
        icon: "⏳",
      },
      error: {
        label: "Error",
        className: "badge-clean badge-red",
        icon: "⚠",
      },
      rejected: {
        label: "Rechazado",
        className: "badge-clean badge-red",
        icon: "✗",
      },
      rechazado: {
        label: "Rechazado",
        className: "badge-clean badge-red",
        icon: "✗",
      },
    };
    const status = statusMap[value] || statusMap.pending;
    return (
      <Badge className={status.className}>
        <span className="mr-1">{status.icon}</span>
        {status.label}
      </Badge>
    );
  }

  // Validation status formatting
  if (key === "validation_status") {
    const statusMap = {
      Validado: {
        label: "Validado",
        className: "badge-clean badge-green",
        icon: "✓",
      },
      Aprobado: {
        label: "Aprobado",
        className: "badge-clean badge-green",
        icon: "✓",
      },
      Pendiente: {
        label: "Pendiente",
        className: "badge-clean badge-amber",
        icon: "⏳",
      },
      Rechazado: {
        label: "Rechazado",
        className: "badge-clean badge-red",
        icon: "✗",
      },
    };
    const status = statusMap[value] || statusMap["Pendiente"];
    return (
      <Badge className={status.className}>
        <span className="mr-1">{status.icon}</span>
        {status.label}
      </Badge>
    );
  }

  // Step formatting
  if (key === "step") {
    const stepMap = {
      Requests: {
        label: "Solicitud",
        className: "badge-clean badge-blue",
        icon: "📋",
      },
      Suppliers: {
        label: "Proveedores",
        className: "badge-clean badge-orange",
        icon: "🏢",
      },
      "PO Created": {
        label: "Orden Creada",
        className: "badge-clean badge-green",
        icon: "📄",
      },
      "Delivery & Reconcilation": {
        label: "Entrega",
        className: "badge-clean badge-blue",
        icon: "🚚",
      },
    };
    const step = stepMap[value] || {
      label: value,
      className: "badge-clean badge-slate",
      icon: "📌",
    };
    return (
      <Badge className={step.className}>
        <span className="mr-1">{step.icon}</span>
        {step.label}
      </Badge>
    );
  }

  // Monetary values formatting
  if (
    (key === "total_amount" || 
     key === "precio_aproximado" || 
     key === "amount" ||
     key === "monto") &&
    (typeof value === "string" || typeof value === "number")
  ) {
    return (
      <div className="flex items-center font-medium text-slate-900 dark:text-slate-100">
        <DollarSign className="h-4 w-4 mr-1 text-green-600" />
        {formatMoney(value, 2)}
      </div>
    );
  }

  // Date formatting
  if (
    (key === "extraction_date" ||
      key === "created_at" ||
      key === "createdAt" ||
      key === "deliveryDate" ||
      key === "delivery_target_date" ||
      key.includes("fecha") ||
      key.includes("date")) &&
    value
  ) {
    let formattedDate;
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        formattedDate = value;
      } else {
        formattedDate = date.toLocaleDateString("es-ES", {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    } catch (error) {
      formattedDate = value;
    }

    return (
      <div className="flex items-center text-sm text-slate-600 dark:text-slate-400 min-w-32">
        <Calendar className="h-4 w-4 mr-2 text-slate-400 flex-shrink-0" />
        <span className="truncate">{formattedDate}</span>
      </div>
    );
  }

  // ID formatting
  if (key === "id" || key === "friendly_id") {
    return (
      <div className="font-mono text-sm bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded border max-w-48 truncate">
        {String(value)}
      </div>
    );
  }

  // Origin channel formatting
  if (key === "origin_channel") {
    const channelMap = {
      form: {
        label: "Formulario",
        className: "badge-clean badge-blue",
        icon: "📝",
      },
      whatsapp: {
        label: "WhatsApp",
        className: "badge-clean badge-green",
        icon: "💬",
      },
      email: {
        label: "Email",
        className: "badge-clean badge-orange",
        icon: "📧",
      },
    };
    const channel = channelMap[value?.toLowerCase()] || {
      label: value,
      className: "badge-clean badge-slate",
      icon: "📱",
    };
    return (
      <Badge className={channel.className}>
        <span className="mr-1">{channel.icon}</span>
        {channel.label}
      </Badge>
    );
  }

  // Created by formatting
  if (key === "created_by" || key === "created_by_name" || key === "requester") {
    let displayName = value;

    // If the value contains an ID (format: "name (id)"), extract only the name
    if (
      typeof value === "string" &&
      value.includes("(") &&
      value.includes(")")
    ) {
      displayName = value.replace(/\s*\([^)]*\)/, "").trim();
    }

    return (
      <div className="flex items-center min-w-32">
        <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white text-xs font-semibold mr-3 flex-shrink-0">
          {displayName.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
            {displayName}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Creado por
          </div>
        </div>
      </div>
    );
  }

  // Area/Department formatting
  if (key === "area" || key === "department") {
    return (
      <div className="text-sm">
        <div className="font-medium text-slate-900 dark:text-slate-100 truncate max-w-32">
          {value}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">Área</div>
      </div>
    );
  }

  // Title formatting
  if (key === "title") {
    return (
      <div className="max-w-sm">
        <div className="font-medium text-slate-900 dark:text-slate-100 text-sm line-clamp-2">
          {value}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Título del IPR
        </div>
      </div>
    );
  }

  // Source email formatting
  if (key === "source_email") {
    return (
      <div className="text-sm max-w-48">
        <div className="font-mono text-blue-600 dark:text-blue-400 truncate">
          {value}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Email origen
        </div>
      </div>
    );
  }

  // Language formatting
  if (key === "language") {
    const languageMap = {
      es: "🇪🇸 Español",
      en: "🇺🇸 Inglés",
      pt: "🇧🇷 Portugués",
      fr: "🇫🇷 Francés",
    };

    return (
      <div className="text-sm">
        <div className="font-medium text-slate-900 dark:text-slate-100">
          {languageMap[value.toLowerCase()] || value}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Idioma
        </div>
      </div>
    );
  }

  // Category/Subcategory formatting
  if (key === "category" || key === "subcategory") {
    return (
      <div className="text-sm">
        <div className="font-medium text-slate-900 dark:text-slate-100">
          {value}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {key === "category" ? "Categoría" : "Subcategoría"}
        </div>
      </div>
    );
  }

  // Justification formatting
  if (key === "justification") {
    return (
      <div className="text-sm max-w-xs">
        <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
          {value}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Justificación
        </div>
      </div>
    );
  }

  // Currency formatting
  if (key === "currency") {
    return (
      <div className="text-sm">
        <div className="font-medium text-slate-900 dark:text-slate-100">
          {value}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Moneda
        </div>
      </div>
    );
  }

  // Criticality formatting
  if (key === "criticality") {
    const criticalityMap = {
      baja: {
        label: "Baja",
        className: "badge-clean badge-green",
        icon: "🟢",
      },
      media: {
        label: "Media",
        className: "badge-clean badge-amber",
        icon: "🟡",
      },
      alta: {
        label: "Alta",
        className: "badge-clean badge-red",
        icon: "🔴",
      },
    };
    const criticality = criticalityMap[value?.toLowerCase()] || {
      label: value,
      className: "badge-clean badge-slate",
      icon: "⚪",
    };
    return (
      <Badge className={criticality.className}>
        <span className="mr-1">{criticality.icon}</span>
        {criticality.label}
      </Badge>
    );
  }

  // Object formatting
  if (typeof value === "object") {
    return (
      <span className="text-slate-400 dark:text-slate-500 italic">
        Objeto
      </span>
    );
  }

  // Default formatting for any other field
  return (
    <div className="text-sm max-w-xs">
      <div className="text-slate-900 dark:text-slate-100 truncate">
        {String(value)}
      </div>
    </div>
  );
};

/**
 * Simple formatter for values without JSX components (for plain text display)
 */
export const formatValueSimple = (key, value) => {
  if (value === null || value === undefined) {
    return "Sin datos";
  }

  // Monetary values
  if (
    (key === "total_amount" || 
     key === "precio_aproximado" || 
     key === "amount" ||
     key === "monto") &&
    (typeof value === "string" || typeof value === "number")
  ) {
    return "$" + formatMoney(value, 2);
  }

  // Date values
  if (
    (key === "extraction_date" ||
      key === "created_at" ||
      key === "createdAt" ||
      key === "deliveryDate" ||
      key === "delivery_target_date" ||
      key.includes("fecha") ||
      key.includes("date")) &&
    value
  ) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("es-ES", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        });
      }
    } catch (error) {
      // Fall through to return original value
    }
  }

  return String(value);
};
